// Phase C1 — Membership 2.0 registration + SMS password recovery.
// Three-screen flow driven by a single page component with local `step` state:
//   1. IdentityStep   → collect name/email/phone/password → POST /auth/register/start
//   2. VerifyStep     → 6-digit SMS code entry           → POST /auth/register/verify
//   3. Success        → auto sign-in + redirect to /welcome
//
// The forgot-password page reuses the same VerifyStep shell.
// If the backend reports Verify Service is not configured, we degrade
// gracefully to a coming-soon notice on the entry screen.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authAPI, tokenStore } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import rippersClubLogo from '../assets/rippers-club-logo.png';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { refreshUser: refresh } = useAuth();
  const [step, setStep] = useState('form'); // form | verify | done
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', gender: '',
    password: '', confirm_password: '',
  });
  const [pending, setPending] = useState(null); // { pending_id, phone_masked, expires_in_seconds }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [smsConfigured, setSmsConfigured] = useState(true);

  useEffect(() => {
    authAPI.verifyStatus()
      .then((r) => setSmsConfigured(!!r.data?.configured))
      .catch(() => setSmsConfigured(true));  // fall through and let start() surface any error
  }, []);

  const submitForm = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.gender) { setError('Please select your gender to continue.'); return; }
    if (form.password !== form.confirm_password) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      const r = await authAPI.registerStart(form);
      setPending(r.data);
      setStep('verify');
    } catch (e2) {
      console.error('[c1] registerStart failed', e2?.response?.status, e2?.response?.data, e2);
      const detail = e2?.response?.data?.detail;
      const status = e2?.response?.status;
      if (detail) setError(detail);
      else if (status === 429) setError("You've requested too many codes. Please wait a few minutes and try again.");
      else if (status === 503) setError('Registration is temporarily unavailable. Please try again shortly.');
      else setError('Registration is temporarily unavailable. Please try again shortly.');
    } finally { setBusy(false); }
  };

  const submitCode = async (code) => {
    setError('');
    setBusy(true);
    try {
      const r = await authAPI.registerVerify({ pending_id: pending.pending_id, code });
      if (r.data?.session_token) tokenStore.set(r.data.session_token);
      try { await refresh(); } catch (refErr) { console.warn('[c1] auth refresh failed', refErr); }
      toast.success('Welcome to Rippers Club!');
      navigate('/complete-profile', { replace: true });
    } catch (e) {
      console.error('[c1] registerVerify failed', e?.response?.status, e?.response?.data, e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (detail) setError(detail);
      else if (status === 410) setError('That verification session expired. Please start over.');
      else if (status === 429) setError("Too many attempts. Please wait a few minutes and try again.");
      else setError('That code is incorrect or expired. Please try again.');
    } finally { setBusy(false); }
  };

  const restart = () => { setPending(null); setStep('form'); setError(''); };

  return (
    <AuthShell title="Create your free Rippers Club account"
               subtitle={step === 'form' ? 'Takes about 60 seconds. Verify by SMS.' : `Enter the 6-digit code we sent to ${pending?.phone_masked || 'your phone'}`}>
      {step === 'form' && !smsConfigured && (
        <div className="mb-4 rounded-lg border border-[#c9a93a]/60 bg-[#fffbf0] p-3 text-[12.5px] text-[#5b4b16]" data-testid="sms-not-configured">
          Public registration is temporarily unavailable while SMS verification is being configured.
          Please <Link to="/join" className="underline">use your invite</Link> or contact an admin.
        </div>
      )}
      {step === 'form' && (
        <form onSubmit={submitForm} className="space-y-3" data-testid="c1-register-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" value={form.first_name}
                   onChange={(v) => setForm({ ...form, first_name: v })}
                   required autoComplete="given-name" testid="reg-first-name" />
            <Field label="Last name" value={form.last_name}
                   onChange={(v) => setForm({ ...form, last_name: v })}
                   required autoComplete="family-name" testid="reg-last-name" />
          </div>
          <Field label="Email" type="email" value={form.email}
                 onChange={(v) => setForm({ ...form, email: v })}
                 required autoComplete="email" testid="reg-email" />
          <Field label="Mobile number" type="tel" value={form.phone}
                 onChange={(v) => setForm({ ...form, phone: v })}
                 required autoComplete="tel" placeholder="(714) 555-1234"
                 hint="Any format works — we'll send a one-time code by SMS."
                 testid="reg-phone" />
          <div>
            <label className="block text-[12px] font-medium text-white mb-1.5">
              Gender <span className="text-[#c9a93a]">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2" data-testid="reg-gender">
              {['male', 'female'].map((g) => (
                <button key={g} type="button"
                        data-testid={`reg-gender-${g}`}
                        onClick={() => setForm({ ...form, gender: g })}
                        className={`py-3 rounded-lg border text-[14px] font-medium transition ${
                          form.gender === g
                            ? 'bg-[#c9a93a] text-[#013523] border-[#c9a93a]'
                            : 'bg-transparent text-white border-white/25 hover:border-white/50 hover:bg-white/[0.04]'
                        }`}>
                  {g === 'male' ? 'Male' : 'Female'}
                </button>
              ))}
            </div>
          </div>
          <Field label="Password" type="password" value={form.password}
                 onChange={(v) => setForm({ ...form, password: v })}
                 required autoComplete="new-password" hint="At least 8 characters"
                 testid="reg-password" />
          <Field label="Confirm password" type="password" value={form.confirm_password}
                 onChange={(v) => setForm({ ...form, confirm_password: v })}
                 required autoComplete="new-password" testid="reg-confirm-password" />
          {error && <ErrorNote text={error} testid="reg-error" />}
          <button type="submit" disabled={busy || !smsConfigured}
                  data-testid="reg-submit"
                  className="w-full py-3 rounded-full bg-[#c9a93a] text-[#013523] font-medium hover:bg-[#daa64a] disabled:opacity-60">
            {busy ? 'Sending code…' : 'Send verification code'}
          </button>
        </form>
      )}
      {step === 'verify' && (
        <CodeStep phoneMasked={pending?.phone_masked} onSubmit={submitCode}
                  onEditNumber={restart} busy={busy} error={error} testidPrefix="reg" />
      )}
      <p className="mt-6 text-[12px] text-white/60 text-center">
        Already a member? <Link to="/login" className="text-white/85 hover:underline" data-testid="reg-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}


// ---------------- Forgot-password (SMS-based) --------------------------------
export function ForgotPasswordPageC1() {
  const navigate = useNavigate();
  const { refreshUser: refresh } = useAuth();
  const [step, setStep] = useState('identifier');  // identifier | code | new_password | done
  const [identifier, setIdentifier] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [neutralNote, setNeutralNote] = useState('');
  const [error, setError] = useState('');

  const submitIdentifier = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await authAPI.resetStart({ identifier });
      setNeutralNote(r.data?.message || '');
      setStep('code');
    } catch (e2) {
      console.error('[c1] resetStart failed', e2?.response?.status, e2?.response?.data, e2);
      setError('Password recovery is temporarily unavailable. Please try again shortly.');
    } finally { setBusy(false); }
  };

  const submitCode = async (submittedCode) => {
    setError(''); setBusy(true);
    try {
      const r = await authAPI.resetVerify({ identifier, code: submittedCode });
      setResetToken(r.data?.reset_token);
      setStep('new_password');
    } catch (e) {
      console.error('[c1] resetVerify failed', e?.response?.status, e?.response?.data, e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (detail) setError(detail);
      else if (status === 410) setError('That verification session expired. Please start over.');
      else if (status === 429) setError("Too many attempts. Please wait a few minutes and try again.");
      else setError('That code is incorrect or expired. Please try again.');
    } finally { setBusy(false); }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      const r = await authAPI.resetComplete({ reset_token: resetToken, new_password: newPassword, confirm_password: confirmPassword });
      if (r.data?.session_token) tokenStore.set(r.data.session_token);
      try { await refresh(); } catch (refErr) { console.warn('[c1] auth refresh failed', refErr); }
      toast.success('Your password has been updated.');
      navigate('/', { replace: true });
    } catch (e) {
      console.error('[c1] resetComplete failed', e?.response?.status, e?.response?.data, e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (detail) setError(detail);
      else if (status === 410) setError('That reset link has expired. Please start over.');
      else setError('Password reset failed. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Forgot your password?"
               subtitle={step === 'identifier'    ? "We'll send a code to the mobile number on file." :
                        step === 'code'          ? neutralNote :
                        step === 'new_password'  ? 'Create a new password.' : ''}>
      {step === 'identifier' && (
        <form onSubmit={submitIdentifier} className="space-y-3" data-testid="c1-forgot-form">
          <Field label="Email or mobile number" value={identifier}
                 onChange={setIdentifier} required testid="forgot-identifier" />
          {error && <ErrorNote text={error} testid="forgot-error" />}
          <button type="submit" disabled={busy}
                  data-testid="forgot-submit"
                  className="w-full py-3 rounded-full bg-[#c9a93a] text-[#013523] font-medium hover:bg-[#daa64a] disabled:opacity-60">
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      )}
      {step === 'code' && (
        <CodeStep phoneMasked=""
                  onSubmit={submitCode}
                  onEditNumber={() => setStep('identifier')}
                  busy={busy} error={error}
                  submitLabel="Verify code"
                  testidPrefix="forgot" />
      )}
      {step === 'new_password' && (
        <form onSubmit={submitNewPassword} className="space-y-3" data-testid="c1-newpassword-form">
          <Field label="New password" type="password" value={newPassword}
                 onChange={setNewPassword} required autoComplete="new-password"
                 hint="At least 8 characters" testid="forgot-newpassword" />
          <Field label="Confirm new password" type="password" value={confirmPassword}
                 onChange={setConfirmPassword} required autoComplete="new-password"
                 testid="forgot-confirmpassword" />
          {error && <ErrorNote text={error} testid="forgot-newpassword-error" />}
          <button type="submit" disabled={busy}
                  data-testid="forgot-newpassword-submit"
                  className="w-full py-3 rounded-full bg-[#c9a93a] text-[#013523] font-medium hover:bg-[#daa64a] disabled:opacity-60">
            {busy ? 'Updating…' : 'Update password and sign in'}
          </button>
        </form>
      )}
      <p className="mt-6 text-[12px] text-white/60 text-center">
        Remembered it? <Link to="/login" className="text-white/85 hover:underline">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}


// ---------------- Small shared UI --------------------------------------------
function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-10"
         style={{ backgroundColor: '#013523' }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img src={rippersClubLogo} alt="Rippers Club"
               className="h-16 w-auto object-contain drop-shadow-[0_0_40px_rgba(201,169,58,0.35)]"
               draggable={false} />
          <h1 className="mt-4 font-serif text-white text-2xl sm:text-3xl text-center leading-tight">{title}</h1>
          {subtitle && <p className="mt-1 text-[12.5px] text-white/70 text-center">{subtitle}</p>}
        </div>
        <div className="bg-white/[0.06] backdrop-blur border border-white/12 rounded-2xl p-6 shadow-2xl">
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, testid, onChange, ...props }) {
  return (
    <label className="block">
      <span className="block text-[11.5px] text-white/70 mb-1">{label}</span>
      <input {...props}
             data-testid={testid}
             onChange={(e) => onChange(e.target.value)}
             className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-[14px] placeholder-white/30 focus:outline-none focus:border-[#c9a93a]" />
      {hint && <span className="block text-[10.5px] text-white/45 mt-1">{hint}</span>}
    </label>
  );
}

function ErrorNote({ text, testid }) {
  return (
    <div role="alert" data-testid={testid}
         className="flex items-start gap-2 rounded-lg bg-[#3b1414] border border-[#8f2a2a]/60 px-3 py-2 text-[12.5px] text-[#ffd4d4]">
      <span aria-hidden className="text-[#f28c8c]">⚠</span>
      <span>{text}</span>
    </div>
  );
}

// 6-digit code input. Renders one accessible input with autoComplete=one-time-code.
function CodeStep({ phoneMasked, onSubmit, onEditNumber, busy, error, submitLabel = 'Verify', testidPrefix = 'code' }) {
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(60);
  const ref = useRef(null);

  useEffect(() => { ref.current?.focus(); }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) return;
    onSubmit(code);
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${testidPrefix}-code-form`}>
      {phoneMasked && (
        <p className="text-[12.5px] text-white/80 text-center">
          Code sent to <span className="tabular-nums">{phoneMasked}</span>
        </p>
      )}
      <input
        ref={ref}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="123456"
        aria-label="6-digit verification code"
        data-testid={`${testidPrefix}-code-input`}
        className="w-full text-center tracking-[0.35em] font-serif text-white text-3xl py-3 rounded-lg bg-white/10 border border-white/25 focus:outline-none focus:border-[#c9a93a]" />
      {error && <ErrorNote text={error} testid={`${testidPrefix}-code-error`} />}
      <button type="submit" disabled={busy || code.length !== 6}
              data-testid={`${testidPrefix}-code-submit`}
              className="w-full py-3 rounded-full bg-[#c9a93a] text-[#013523] font-medium hover:bg-[#daa64a] disabled:opacity-60">
        {busy ? 'Verifying…' : submitLabel}
      </button>
      <div className="flex items-center justify-between text-[11.5px] text-white/60">
        <button type="button" onClick={onEditNumber} className="hover:text-white underline underline-offset-2" data-testid={`${testidPrefix}-code-edit`}>
          Wrong number?
        </button>
        <span aria-live="polite">
          {cooldown > 0 ? `Resend in ${cooldown}s` : (
            <button type="button" onClick={() => { setCooldown(60); onEditNumber(); }}
                    className="hover:text-white underline underline-offset-2" data-testid={`${testidPrefix}-code-resend`}>
              Resend code
            </button>
          )}
        </span>
      </div>
    </form>
  );
}
